import { ITimetable } from 'src/api/getTimetables';
import { ITimetableSchedule } from 'src/api/getTimetable';
import { IMenu } from 'src/types/menu.types';
import { IMenuSet } from 'src/api/getMenuSets';
import { filterMenusByRanges, filterMenuSetsByRanges, isMenuTrackSchedule } from './helpers';

interface ProposedDefaults {
  // A menu's or a menu set's id - the menu track's default can be either.
  defaultMenuId: string;
  defaultPriceSchemeId: string;
  ranges: ITimetable['ranges'];
  menus: IMenu[];
  menuSets?: IMenuSet[];
}

/**
 * Flags a schedule that would fall back to the timetable's default - because it currently stands
 * in for it (colour_order === null, the same ground-truth signal resolveAffectedScheduleBlocks
 * uses), or because its own menu no longer belongs to the newly selected ranges - but has no
 * valid proposed default to actually fall back to yet. Such a schedule isn't "changed" by
 * resolveAffectedScheduleBlocks (there's nothing to reassign it to, so it's left as-is), but it's
 * no longer a trustworthy entity either, so the preview needs its own signal to warn about it.
 */
export const getInvalidDefaultScheduleIds = (
  schedules: ITimetableSchedule[],
  { defaultMenuId, defaultPriceSchemeId, ranges, menus, menuSets = [] }: ProposedDefaults,
): Set<string> => {
  const rangeIdsSubmitted = ranges.length > 0;
  const menuTrackIdsValidForRanges = new Set([
    ...filterMenusByRanges(menus, ranges).map((menu) => menu.menuId),
    ...filterMenuSetsByRanges(menuSets, ranges).map((menuSet) => menuSet.menu_set_id),
  ]);

  const fallsBackToAnUnchosenDefault = (schedule: ITimetableSchedule): boolean => {
    const isOnOldDefault = schedule.colour_order === null;

    if (isMenuTrackSchedule(schedule)) {
      const entityId = schedule.menu_id ?? schedule.menu_set_id;
      const isInvalidForNewRanges =
        !isOnOldDefault && rangeIdsSubmitted && !menuTrackIdsValidForRanges.has(entityId as string);
      return (isOnOldDefault || isInvalidForNewRanges) && defaultMenuId === '';
    }

    return isOnOldDefault && defaultPriceSchemeId === '';
  };

  return new Set(schedules.filter(fallsBackToAnUnchosenDefault).map((schedule) => schedule.schedule_id));
};
